<template>
  <Teleport to="body">
    <Transition name="sidebar">
      <div
        v-if="isOpen"
        class="app-sidebar"
        :class="{ 'app-sidebar--open': isOpen }"
      >
        <div class="app-sidebar__overlay" @click="handleClose" />

        <aside class="app-sidebar__panel">
          <div class="app-sidebar__header">
            <RouterLink
              class="app-sidebar__profile"
              to="/profile"
              @click="handleClose"
            >
              <div class="app-sidebar__avatar">
                <img
                  v-if="avatarPhoto"
                  :src="avatarPhoto"
                  :alt="$t('nav.profile')"
                />
                <span v-else class="app-sidebar__avatar-placeholder">{{ profileInitials }}</span>
              </div>
              <div class="app-sidebar__profile-info">
                <span class="app-sidebar__name">{{ fullName }}</span>
                <span class="app-sidebar__email">{{ email }}</span>
              </div>
            </RouterLink>

            <button
              class="app-sidebar__close"
              type="button"
              :aria-label="$t('common.close')"
              @click="handleClose"
            >
              <i class="zmdi zmdi-close" />
            </button>
          </div>

          <nav class="app-sidebar__nav">
            <AppNavigationItem
              v-for="item in menuItems"
              :key="item.name"
              class="app-sidebar__link"
              :class="{ 'app-sidebar__link--active': isActive(item.route) }"
              :to="item.route"
              :active="isActive(item.route)"
              :label="item.label"
              :icon="item.icon"
              @click="handleClose"
            />

            <button
              v-if="!isInTelegram"
              class="app-sidebar__link app-sidebar__link--danger"
              type="button"
              @click="handleLogout"
            >
              <i class="app-sidebar__link-icon zmdi zmdi-power" />
              <span class="app-sidebar__link-label">{{ $t('auth.logout') }}</span>
            </button>

            <a
              v-if="isInTelegram && telegramInstallUrl"
              class="app-sidebar__link"
              :href="telegramInstallUrl"
              target="_blank"
              rel="noopener noreferrer"
              @click="handleClose"
            >
              <i class="app-sidebar__link-icon zmdi zmdi-download" />
              <span class="app-sidebar__link-label">{{ $t('nav.installToHome') }}</span>
            </a>

            <button
              v-else-if="canInstallPwa"
              class="app-sidebar__link"
              type="button"
              @click="openPwaModal"
            >
              <i class="app-sidebar__link-icon zmdi zmdi-download" />
              <span class="app-sidebar__link-label">{{ $t('nav.installToHome') }}</span>
            </button>
          </nav>
        </aside>
      </div>
    </Transition>

    <PwaInstallModal
      :visible="pwaModalVisible"
      @close="pwaModalVisible = false"
    />
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, inject } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import { useProfileStore } from '@/modules/profile/store/profileStore'
import { useLayoutStore } from '@/store/layoutStore'
import { isStandalone } from '@/shared/utils/browser'
import PwaInstallModal from '@/components/common/PwaInstallModal.vue'
import AppNavigationItem from './AppNavigationItem.vue'

const { t } = useI18n()
const route = useRoute()
const profileStore = useProfileStore()
const layoutStore = useLayoutStore()

const { profile } = storeToRefs(profileStore)
const isInTelegram = inject<boolean>('isInTelegram', false)
const telegramBotUsername = (import.meta.env.VITE_GONKAGAME_TELEGRAM_BOT_USERNAME || '').replace(/^@/, '')
const telegramInstallUrl = computed(() => telegramBotUsername
  ? `https://t.me/${telegramBotUsername}/?startapp&addToHomeScreen`
  : ''
)

const isOpen = computed(() => layoutStore.sidebarOpen)
const avatarPhoto = computed(() => profile.value?.photo ?? null)
const fullName = computed(() => profileStore.fullName || t('common.user'))
const email = computed(() => profile.value?.email ?? '')

const profileInitials = computed(() => {
  const name = profileStore.fullName
  if (!name) return 'GG'
  return name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
    .slice(0, 2) || 'GG'
})

const menuItems = computed(() => [
  { name: 'profile', label: t('nav.profile'), route: '/profile', icon: 'zmdi-account' },
  { name: 'affiliate', label: t('nav.affiliate'), route: '/affiliate', icon: 'zmdi-accounts' },
  { name: 'wallet', label: t('nav.wallet'), route: '/wallet', icon: 'zmdi-balance-wallet' },
])

const isActive = (target: string) => route.path.startsWith(target)

const handleClose = () => {
  layoutStore.closeSidebar()
}

const handleLogout = () => {
  handleClose()
  profileStore.logout()
}

const pwaModalVisible = ref(false)
const canInstallPwa = computed(() => !isStandalone())

const openPwaModal = () => {
  handleClose()
  pwaModalVisible.value = true
}
</script>

<style scoped lang="scss">
.app-sidebar {
  position: fixed;
  inset: 0;
  z-index: 150;

  &__overlay {
    position: absolute;
    inset: 0;
    background: var(--ds-scrim);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
  }

  &__panel {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 280px;
    max-width: 85vw;
    background: var(--hm-surface);
    border-left: 1px solid var(--hm-border);
    box-shadow: -8px 0 32px var(--ds-scrim);
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 16px;
    border-bottom: 1px solid var(--hm-border);
  }

  &__profile {
    display: flex;
    align-items: center;
    gap: 12px;
    text-decoration: none;
    flex: 1;
    min-width: 0;
  }

  &__avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    overflow: hidden;
    background: var(--hm-surface-alt);
    border: 2px solid var(--ds-accent-soft-16);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__avatar-placeholder {
    font-size: var(--ds-text-size-body-lg);
    font-weight: var(--ds-weight-semibold);
    color: var(--hm-text-secondary);
  }

  &__profile-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__name {
    font-size: var(--p-text-15);
    font-weight: var(--ds-weight-semibold);
    color: var(--hm-text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__email {
    font-size: var(--ds-text-size-caption);
    color: var(--hm-text-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__close {
    width: 36px;
    height: 36px;
    border-radius: var(--p-radius-50);
    border: none;
    background: var(--hm-surface-alt);
    color: var(--hm-text-secondary);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s, color 0.2s;
    flex-shrink: 0;

    i {
      font-size: var(--ds-text-size-h-sm);
    }

    &:hover {
      background: var(--ds-accent-soft-08);
      color: var(--hm-accent);
    }
  }

  &__nav {
    display: flex;
    flex-direction: column;
    padding: 16px 12px;
    gap: 4px;
  }

  &__link {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 16px;
    border-radius: var(--ds-radius-card);
    border: none;
    background: transparent;
    color: var(--hm-text-secondary);
    text-decoration: none;
    font-size: var(--p-text-15);
    font-weight: var(--ds-weight-medium);
    cursor: pointer;
    transition: background 0.2s, color 0.2s;

    &:hover {
      background: var(--ds-accent-soft-08);
      color: var(--hm-text-primary);
    }
  }

  &__link--active {
    background: var(--ds-accent-soft-12);
    color: var(--hm-accent);
  }

  &__link--danger {
    margin-top: auto;
    color: var(--hm-text-muted);

    &:hover {
      background: var(--ds-danger-soft);
      color: var(--hm-danger);
    }
  }

  &__link-icon {
    font-size: var(--ds-text-size-h-sm);
    width: 24px;
    text-align: center;
  }

  &__link-label {
    flex: 1;
  }
}

// Transition animations
.sidebar-enter-active,
.sidebar-leave-active {
  transition: opacity 0.25s ease;

  .app-sidebar__panel {
    transition: transform 0.25s ease;
  }
}

.sidebar-enter-from,
.sidebar-leave-to {
  opacity: 0;

  .app-sidebar__panel {
    transform: translateX(100%);
  }
}
</style>
